<template>
  <div>
    <!-- Cabeçalho -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h5 class="mb-0">Perfis de Usuários</h5>
      <router-link
        v-if="hasPermission('adicionar_perfis_usuarios')"
        to="/configuracoes/perfis-de-usuarios/novo"
      >
        <b-button variant="primary" class="d-flex align-items-center">
          <i class="bi bi-person-plus-fill mr-2" title="Novo Perfil"></i>
        </b-button>
      </router-link>
    </div>

    <!-- Filtros -->
    <div class="d-flex mb-3" style="gap: 10px;">
      <input
        v-model="filters.nome"
        placeholder="Filtrar por nome"
        class="form-control mr-2"
        @input="filtrar"
      />
      <input
        v-model="filters.descricao"
        placeholder="Filtrar por descrição"
        class="form-control mr-2"
        @input="filtrar"
      />
      <b-button
        variant="secondary"
        @click="limparFiltros"
        class="d-flex align-items-center justify-content-center"
      >
        <i class="bi bi-funnel mr-2" title="Limpar Filtro"></i>
      </b-button>
    </div>

    <!-- Tabela customizada -->
    <table class="table table-striped table-hover">
      <thead>
        <tr>
          <th>
            <button class="btn btn-link p-0" @click="changeSort('nome')">
              Nome {{ arrow('nome') }}
            </button>
          </th>
          <th>
            <button class="btn btn-link p-0" @click="changeSort('descricao')">
              Descrição {{ arrow('descricao') }}
            </button>
          </th>
          <th>
            <button class="btn btn-link p-0" @click="changeSort('peso')">
              Peso {{ arrow('peso') }}
            </button>
          </th>
          <th class="text-end">Ações</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="p in perfis" :key="p.id">
          <td>{{ p.nome }}</td>
          <td>{{ p.descricao }}</td>
          <td>{{ p.peso }}</td>
          <td class="text-end">
            <router-link
              v-if="hasPermission('detalhar_perfis_usuarios')"
              :to="`/configuracoes/perfis-de-usuarios/${p.id}/detalhar`"
            >
              <i class="bi bi-eye-fill text-primary mx-2" title="Detalhar"></i>
            </router-link>
            <router-link
              v-if="hasPermission('alterar_perfis_usuarios')"
              :to="`/configuracoes/perfis-de-usuarios/${p.id}`"
            >
              <i class="bi bi-pencil-fill text-warning mx-2" title="Editar"></i>
            </router-link>
            <i
              v-if="hasPermission('excluir_perfis_usuarios')"
              class="bi bi-trash-fill text-danger mx-2"
              title="Excluir"
              @click="excluir(p.id)"
            ></i>
          </td>
        </tr>

        <tr v-if="!perfis.length">
          <td colspan="4" class="text-center text-muted py-4">
            Nenhum perfil encontrado.
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Paginação -->
    <div class="d-flex justify-content-between align-items-center mt-3">
      <div>
        Mostrando {{ startRow }} a {{ endRow }} de {{ totalItemsVisiveis }} registros
      </div>

      <div class="d-flex align-items-center" style="gap: .5rem;">
        <b-form-select
          v-model="limit"
          :options="pageOptions.map(n => ({ value: n, text: n + ' / página' }))"
          size="sm"
          style="width: 6rem"
          @change="changeLimit"
        />
      </div>

      <div class="d-flex align-items-center">
        <b-pagination
          v-model="page"
          :per-page="limit"
          :total-rows="totalItems"
          size="sm"
          limit="5"
          @input="load"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { BButton, BPagination, BFormSelect } from 'bootstrap-vue-next'
import axios from '@/services/axios'
import { useSortableTable } from '@/utils/useSortableTable'
import { usePermissions } from '@/composables/usePermissions'

const { fetchPermissions, hasPermission } = usePermissions()

const {
  items,
  totalItems,
  page,
  limit,
  filters,
  load,
  sort: changeSort,
  arrow
} = useSortableTable(
  `/configuracoes/perfis-de-usuarios`,
  { key: 'nome', order: 'asc' }
)

const pageOptions = ref([10, 15, 20, 25, 50])

const perfis = computed(() =>
  (items.value || []).filter(p => p.nome !== 'Super Administrador')
)

const totalItemsVisiveis = computed(() => perfis.value.length)

const startRow = computed(() => {
  if (totalItemsVisiveis.value === 0) return 0
  return (page.value - 1) * limit.value + 1
})

const endRow = computed(() => {
  if (totalItemsVisiveis.value === 0) return 0
  return startRow.value + perfis.value.length - 1
})

function filtrar() {
  page.value = 1
  load()
}

function limparFiltros() {
  filters.value.nome = ''
  filters.value.descricao = ''
  page.value = 1
  load()
}

function changeLimit() {
  page.value = 1
  load()
}

async function excluir(id) {
  if (confirm('Tem certeza que deseja excluir este perfil?')) {
    await axios.delete(`/configuracoes/perfis-de-usuarios/${id}`)
    load()
  }
}

watch(page, () => {
  load()
})

watch(limit, () => {
  page.value = 1
  load()
})

onMounted(async () => {
  await fetchPermissions()
  filters.value.nome = ''
  filters.value.descricao = ''
  load()
})
</script>

<style scoped>
:deep(.pagination.pagination-sm .page-link) {
  line-height: 1;
  font-size: 0.8rem !important;
  border-radius: 0 !important;
}

.table-hover tr:hover {
  background-color: var(--bs-light);
}
</style>